import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SignupForm } from "@/components/auth/SignupForm";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { useAuth } from "@/contexts/AuthContext";
import { showErrorToast } from "@/utils/toast-utils";
import { useQueryClient } from "@tanstack/react-query";
import { usePendingTestCreation } from "@/hooks/usePendingTestCreation";
import { Button } from "@/components/ui/button";
import { RoleSelector } from "@/components/auth/RoleSelector";
import { Separator } from "@/components/ui/separator";
import { trackGoogleAuth, trackLinkedInAuth } from "@/utils/auth-tracking";
import { logAuthState, checkAuthRaceConditions } from "@/utils/debug-auth";

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    user,
    authStateReady
  } = useAuth();
  const queryClient = useQueryClient();
  const {
    processPendingTest,
    isCreating
  } = usePendingTestCreation();
  const testProcessedRef = useRef(false);
  const returnToFromState = location.state?.returnTo;
  const returnToFromParams = searchParams.get('returnTo');
  const returnTo = returnToFromState || returnToFromParams;
  const enforceEmployerRole = location.state?.enforceEmployerRole;
  const isTestInvite = returnTo?.includes('/test/invite/');
  const pendingTestInviteToken = sessionStorage.getItem('pendingTestInviteToken');
  const pendingTestId = sessionStorage.getItem('pendingTestId');
  const pendingTestDetails = sessionStorage.getItem('pendingTestDetails');
  const pendingTestQuestions = sessionStorage.getItem('pendingTestQuestions');
  const isCreatingTest = sessionStorage.getItem('isCreatingTest') === 'true';

  // Debug on component mount
  useEffect(() => {
    console.log("[Signup] Component mounted, auth state:", {
      user: !!user,
      authStateReady,
      returnTo
    });

    // Debug auth state after a short delay
    setTimeout(() => {
      logAuthState();
      checkAuthRaceConditions();
    }, 500);
  }, []);
  
  console.log("[Signup] Page loaded with location state:", location.state);
  console.log("[Signup] returnTo path:", returnTo);
  console.log("[Signup] isTestInvite:", isTestInvite);
  console.log("[Signup] pendingTestInviteToken:", pendingTestInviteToken);
  console.log("[Signup] pendingTestId:", pendingTestId);
  console.log("[Signup] isCreatingTest flag:", isCreatingTest);
  console.log("[Signup] enforceEmployerRole:", enforceEmployerRole);
  console.log("[Signup] pendingTestDetails exists:", !!pendingTestDetails);
  console.log("[Signup] pendingTestQuestions exists:", !!pendingTestQuestions);
  
  const initialSelectedRole = isTestInvite || pendingTestInviteToken ? 'candidate' : enforceEmployerRole || returnTo === '/test-creator' && isCreatingTest ? 'employer' : null;
  const [selectedRole, setSelectedRole] = useState<"candidate" | "employer" | null>(initialSelectedRole);
  
  useEffect(() => {
    if (!user || testProcessedRef.current) return;
    const handleUserAuthenticated = async () => {
      testProcessedRef.current = true;
      console.log("[Signup] User authenticated:", user.id);
      console.log("[Signup] User metadata:", user.user_metadata);
      console.log("[Signup] User role:", user.user_metadata?.role);
      const testDetails = sessionStorage.getItem('pendingTestDetails');
      const testQuestions = sessionStorage.getItem('pendingTestQuestions');
      const pendingTestInviteToken = sessionStorage.getItem('pendingTestInviteToken');
      const pendingTestId = sessionStorage.getItem('pendingTestId');
      const shouldRedirectToJob = sessionStorage.getItem('redirectToJobPage') === 'true';
      if (isCreatingTest && testDetails && testQuestions) {
        console.log("[Signup] User is authenticated and has a pending test, processing it");
        try {
          const parsedDetails = JSON.parse(testDetails);
          if (!parsedDetails.title || !parsedDetails.description) {
            throw new Error("Invalid test data: Missing required fields");
          }

          // Increased timeout to allow profile role to be fully established
          setTimeout(() => {
            processPendingTest(user.id).then(result => {
              if (result) {
                console.log("[Signup] Successfully processed pending test");
              } else {
                console.log("[Signup] No pending test to process or failed to process");
                navigateAfterSignup();
              }
            }).catch(err => {
              console.error("[Signup] Error processing test:", err);
              navigateAfterSignup();
            });
          }, 2000); // Increased to 2000ms to ensure profile is fully created
        } catch (error) {
          console.error("[Signup] Invalid test data:", error);
          navigateAfterSignup();
        }
      } else if (pendingTestId && shouldRedirectToJob) {
        console.log("[Signup] User authenticated with pending test invite, redirecting to job page");
        navigate(`/job/${pendingTestId}`, {
          replace: true
        });

        // Clear session storage after redirect
        sessionStorage.removeItem('pendingTestInviteToken');
        sessionStorage.removeItem('pendingTestId');
        sessionStorage.removeItem('redirectToJobPage');
      } else {
        navigateAfterSignup();
      }
    };

    // Add a delay to allow profile to be fully created
    setTimeout(() => {
      handleUserAuthenticated();
    }, 1500); // Increased to 1500ms
  }, [user, processPendingTest, navigate]);
  
  const navigateAfterSignup = () => {
    const testId = sessionStorage.getItem('pendingTestId');
    const shouldRedirectToJob = sessionStorage.getItem('redirectToJobPage') === 'true';
    if (testId && shouldRedirectToJob) {
      console.log("[Signup] Navigating to job page after signup:", testId);
      sessionStorage.removeItem('pendingTestInviteToken');
      sessionStorage.removeItem('pendingTestId');
      sessionStorage.removeItem('redirectToJobPage');
      navigate(`/job/${testId}`, {
        replace: true
      });
    } else if (returnTo && returnTo !== '/') {
      console.log("[Signup] Navigating to return URL:", returnTo);
      navigate(returnTo, {
        replace: true
      });
    } else {
      console.log("[Signup] No return URL, navigating to dashboard");
      navigate('/dashboard', {
        replace: true
      });
    }
  };
  
  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log("[Signup] Auth state change event:", event, "with role:", initialSelectedRole);
      if (event === 'SIGNED_IN') {
        if (session) {
          console.log("[Signup] Session metadata:", session.user?.user_metadata);
          console.log("[Signup] User role:", session.user?.user_metadata?.role);
          console.log("[Signup] Signed in successfully, refreshing profile data");
          const isOAuthSignup = session.user?.app_metadata?.provider === 'google' || session.user?.app_metadata?.provider === 'linkedin_oidc';
          const hasRole = !!session.user?.user_metadata?.role;
          if (isOAuthSignup && !hasRole) {
            console.log("[Signup] Detected OAuth auth without role, redirecting to role selection");
            navigate('/role-selection', {
              replace: true,
              state: {
                returnTo
              }
            });
            return;
          }
          queryClient.invalidateQueries({
            queryKey: ['profile']
          });
        }
      } else if (event === 'USER_UPDATED') {
        console.log("[Signup] User updated", session?.user);
        queryClient.invalidateQueries({
          queryKey: ['profile']
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [initialSelectedRole, queryClient, navigate, returnTo]);
  
  const handleSignupError = (error: Error) => {
    console.error("[Signup] Error during signup:", error);

    // Provide user-friendly error messages
    let userMessage = "Failed to create account. Please try again.";
    if (error.message?.includes("Password should contain at least one character")) {
      userMessage = "Password must include lowercase, uppercase, numbers, and special characters.";
    } else if (error.message?.includes("weak_password")) {
      userMessage = "Password must include lowercase, uppercase, numbers, and special characters.";
    } else if (error.message?.includes("already registered")) {
      userMessage = "This email is already registered. Please try logging in instead.";
    }
    showErrorToast({
      title: "Signup Error",
      description: userMessage
    });
  };
  
  const handleSignupComplete = (userData: any) => {
    console.log("[Signup] Signup complete, user data:", userData);
  };
  
  const handleGoogleSignup = async () => {
    try {
      console.log("[Signup] Initiating Google signup");
      setIsLoading(true);

      // Clear any previous auth errors from the URL
      if (window.location.href.includes('error=')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      const shouldBeEmployer = enforceEmployerRole || returnTo === '/test-creator' && sessionStorage.getItem('isCreatingTest') === 'true';

      // Store intended role in session storage
      if (shouldBeEmployer) {
        console.log("[Signup] Setting intended role: employer");
        sessionStorage.setItem('intendedRole', 'employer');
      }

      // Prepare absolute URL for consistent behavior
      const appUrl = window.location.origin;
      const roleSelectionUrl = `${appUrl}/role-selection`;
      const redirectUrl = returnTo ? `${roleSelectionUrl}?returnTo=${encodeURIComponent(returnTo)}` : roleSelectionUrl;
      console.log("[Signup] Redirect URL for Google auth:", redirectUrl);

      // Track the start of Google authentication
      trackGoogleAuth('start', {
        returnTo,
        shouldBeEmployer
      });

      // Persist return target and invite context for OAuth roundtrip
      try {
        const pendingToken = sessionStorage.getItem('pendingTestInviteToken');
        const postAuthTarget = returnTo && returnTo !== '/' ? returnTo : (pendingToken ? `/test/invite/${pendingToken}` : '/dashboard');
        localStorage.setItem('postAuthReturnTo', postAuthTarget);
        const pId = sessionStorage.getItem('pendingTestId');
        const redirectFlag = sessionStorage.getItem('redirectToJobPage');
        if (pendingToken) localStorage.setItem('pendingTestInviteToken', pendingToken);
        if (pId) localStorage.setItem('pendingTestId', pId);
        if (redirectFlag) localStorage.setItem('redirectToJobPage', redirectFlag);
        console.log('[Signup] Stored postAuthReturnTo and pending test context in localStorage');
      } catch (e) {
        console.warn('[Signup] Failed to persist OAuth context', e);
      }
      const {
        error,
        data
      } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline' // Request a refresh token
          }
        }
      });
      if (error) {
        console.error("[Signup] Google signup error:", error);
        trackGoogleAuth('error', {
          error: error.message
        });
        throw error;
      }
      console.log("[Signup] Google signup initiated successfully:", data);
    } catch (err: any) {
      console.error("[Signup] Google signup error:", err);
      showErrorToast({
        title: "Signup Error",
        description: err.message || "Failed to sign up with Google"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkedInSignup = async () => {
    try {
      console.log("[Signup] Initiating LinkedIn signup");
      setIsLoading(true);

      // Clear any previous auth errors from the URL
      if (window.location.href.includes('error=')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
      const shouldBeEmployer = enforceEmployerRole || returnTo === '/test-creator' && sessionStorage.getItem('isCreatingTest') === 'true';

      // Store intended role in session storage
      if (shouldBeEmployer) {
        console.log("[Signup] Setting intended role: employer");
        sessionStorage.setItem('intendedRole', 'employer');
      }

      // Prepare absolute URL for consistent behavior
      const appUrl = window.location.origin;
      const roleSelectionUrl = `${appUrl}/role-selection`;
      const redirectUrl = returnTo ? `${roleSelectionUrl}?returnTo=${encodeURIComponent(returnTo)}` : roleSelectionUrl;
      console.log("[Signup] Redirect URL for LinkedIn auth:", redirectUrl);

      // Track the start of LinkedIn authentication
      trackLinkedInAuth('start', {
        returnTo,
        shouldBeEmployer
      });

      // Persist return target and invite context for OAuth roundtrip
      try {
        const pendingToken = sessionStorage.getItem('pendingTestInviteToken');
        const postAuthTarget = returnTo && returnTo !== '/' ? returnTo : (pendingToken ? `/test/invite/${pendingToken}` : '/dashboard');
        localStorage.setItem('postAuthReturnTo', postAuthTarget);
        const pId = sessionStorage.getItem('pendingTestId');
        const redirectFlag = sessionStorage.getItem('redirectToJobPage');
        if (pendingToken) localStorage.setItem('pendingTestInviteToken', pendingToken);
        if (pId) localStorage.setItem('pendingTestId', pId);
        if (redirectFlag) localStorage.setItem('redirectToJobPage', redirectFlag);
        console.log('[Signup] Stored postAuthReturnTo and pending test context in localStorage');
      } catch (e) {
        console.warn('[Signup] Failed to persist OAuth context', e);
      }
      const {
        error,
        data
      } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: redirectUrl
        }
      });
      if (error) {
        console.error("[Signup] LinkedIn signup error:", error);
        trackLinkedInAuth('error', {
          error: error.message
        });
        throw error;
      }
      console.log("[Signup] LinkedIn signup initiated successfully:", data);
    } catch (err: any) {
      console.error("[Signup] LinkedIn signup error:", err);
      showErrorToast({
        title: "Signup Error",
        description: err.message || "Failed to sign up with LinkedIn"
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const [isLoading, setIsLoading] = useState(false);
  
  if (user && isCreating) {
    return <div className="w-full min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background">
        <div className="w-full max-w-md space-y-4 p-8 bg-card rounded-sm border-border">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">Creating your test</h1>
            <p className="text-muted-foreground mt-2">Please wait while we create your test...</p>
            <div className="flex justify-center mt-4">
              <div className="animate-spin rounded-sm h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </div>
        </div>
      </div>;
  }
  
  return <AuthLayout>
    <div className="w-full max-w-lg">
      <SignupForm 
        selectedRole={selectedRole} 
        onError={handleSignupError} 
        onComplete={handleSignupComplete} 
        enforceEmployerRole={enforceEmployerRole} 
        collectEmployerData={false}
        onGoogleSignup={handleGoogleSignup}
        onLinkedInSignup={handleLinkedInSignup}
        isLoading={isLoading}
      />
    </div>
  </AuthLayout>;
};

export default Signup;